/**
 * V2 rebuild against Figma frame a_start_invite_your_team (393×999).
 * Functional contract preserved: bootstrap team-creation, run-/identity-check,
 * advanceTeamStage on Continue, copy-to-clipboard, error mapping.
 */
import { Box, Snackbar, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError } from '../../api/client';
import {
  isAlreadyInTeam,
  isRunEnded,
  isRunNotStarted,
  isRunPaused,
  isTeamLimitReached,
} from './joinErrorReasons';
import { joinAsGuest } from '../../api/guest';
import { advanceTeamStage } from '../../api/teams';
import { useAuth } from '../../auth/useAuth';
import { fetchMyTeam } from '../../services/publicRuns';
import OnboardingShellV2 from '../../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../../components/ProgressIndicatorV2';
import heroInvite from '../../assets/onboarding/hero-team-invite.svg';
import QrCodeDisplay from '../../components/qr/QrCodeDisplay';
import { useWizardState } from '../../wizard/useWizardState';
import { getStepInfo } from '../../wizard/wizardSteps';
import { pillButtonFocusRing } from '../../styles/onboardingButtons';

const InviteTeamStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { code: rallyeCode } = useParams<{ code: string }>();
  const { checkAuth } = useAuth();
  const wizard = useWizardState();
  const tf = wizard.teamFlow;

  const [copied, setCopied] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<
    'not_started' | 'paused' | 'ended' | 'team_limit' | 'already_in_team' | 'unknown' | null
  >(null);
  const bootstrappedRef = useRef(false);

  const step = getStepInfo('team-invite', 'with-code');

  useEffect(() => {
    if (bootstrappedRef.current) return;
    if (!rallyeCode) return;
    bootstrappedRef.current = true;
    setBootstrapping(true);
    void (async () => {
      try {
        if (tf.teamId && tf.runId) {
          const mine = await fetchMyTeam(rallyeCode);
          const cachedStillMine =
            mine.kind === 'ok' &&
            mine.data.run_id === tf.runId &&
            mine.data.team_id === tf.teamId;
          if (cachedStillMine) {
            wizard.patchTeamFlow({
              teamShareCode: mine.data.team_share_code,
              teamNameFromServer: mine.data.team_name,
            });
            setBootstrapping(false);
            return;
          }
          wizard.resetTeamFlow();
        }
        const resp = await joinAsGuest(rallyeCode, {
          mode: 'create',
          team_name: null,
          display_name: wizard.displayName || undefined,
        });
        wizard.patchTeamFlow({
          teamId: resp.team_id,
          teamShareCode: resp.team_share_code,
          teamNameFromServer: resp.team_name,
          runId: resp.run_id,
          isCreator: true,
        });
        await checkAuth();
      } catch (err) {
        // The run must be active to create/join a team. Map the backend's
        // run-state 422 (from the guest-join or team-create endpoints) to a
        // friendly explanation instead of the generic "try again". Matches
        // both English and German phrasings across both endpoints.
        let mapped:
          | 'not_started'
          | 'paused'
          | 'ended'
          | 'team_limit'
          | 'already_in_team'
          | 'unknown' = 'unknown';
        if (err instanceof ApiError && err.status === 422) {
          // Same shape as team_limit below, one step worse: a participant who
          // is already in a team here cannot fix it themselves at all, and the
          // two exits that look available both ban them from the run (#494).
          if (isAlreadyInTeam(err.detail)) {
            mapped = 'already_in_team';
          }
          // A reached team limit is permanent until the coordination raises it,
          // so "please try again" is not just useless here, it misleads.
          else if (isTeamLimitReached(err.detail)) {
            mapped = 'team_limit';
          } else if (isRunPaused(err.detail)) {
            mapped = 'paused';
          } else if (isRunEnded(err.detail)) {
            mapped = 'ended';
          } else if (isRunNotStarted(err.detail)) {
            mapped = 'not_started';
          }
        }
        setBootstrapError(mapped);
      } finally {
        setBootstrapping(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rallyeCode]);

  const shareCode = tf.teamShareCode ?? '';
  const joinUrl =
    typeof window !== 'undefined' && rallyeCode && shareCode
      ? `${window.location.origin}/join/${rallyeCode}?team=${shareCode}`
      : '';

  const onCopy = async () => {
    if (!shareCode) return;
    try {
      await navigator.clipboard.writeText(shareCode);
      setCopied(true);
    } catch {
      // ignore — clipboard not available in some embedded contexts
    }
  };

  const onContinue = async () => {
    if (tf.teamId && tf.runId && wizard.organizationSlug) {
      try {
        await advanceTeamStage({
          orgSlug: wizard.organizationSlug,
          runId: tf.runId,
          teamId: tf.teamId,
          stage: 'naming',
        });
      } catch (err) {
        console.warn('[InviteTeamStep] advance-stage failed', err);
      }
    }
    navigate(`/join/${rallyeCode}/team/name`);
  };

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlockStart: '40px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroInvite}
          alt=""
          sx={{
            width: '107px',
            height: '98px',
            alignSelf: 'center',
            display: 'block',
            objectFit: 'contain',
            marginBlockEnd: '13px',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
            // headline/small: CSS line-box (32) is 4 px taller than Figma's
            // text-bbox (28). Half-leading puts the glyph 2 px below the line-box
            // top — pull the element up 2 px so the glyph lands at Figma's y=175
            // and the cumulative +4 drift propagating to siblings reduces to +2.
            marginBlockStart: '-2px',
          }}
        >
          {t('invite.title')}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
            marginBlockStart: '-3px',
          }}
        >
          {t('invite.qrHelp')}
        </Typography>
        {bootstrapError && (
          <Typography
            role="alert"
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              color: '#B3261E',
            }}
          >
            {t(`invite.errors.${bootstrapError}`)}
          </Typography>
        )}
        <Box sx={{ alignSelf: 'stretch', marginBlockStart: '3px' }}>
          {joinUrl ? (
            <QrCodeDisplay value={joinUrl} size={282} />
          ) : (
            <Box
              aria-busy={bootstrapping}
              sx={{
                width: '314px',
                height: '314px',
                backgroundColor: '#F5EFF7',
                borderRadius: '8px',
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.5px',
              color: '#322F35',
              // Internal half-leading comp removed — it shrank Frame 37 by 3 px
              // and propagated -3 to "Once all…", landing it 4 px above Figma.
              // Without the comp, Frame 37 stays 88 tall (matches Figma) and
              // "Once all…" / action-row drop into Figma's coordinates.
            }}
          >
            {t('invite.codeFallback')}
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onCopy}
            disabled={!shareCode}
            aria-label={`${shareCode} — ${t('invite.copyAria')}`}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: shareCode ? 'pointer' : 'default',
              alignSelf: 'flex-start',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '24px',
              fontWeight: 600,
              lineHeight: '32px',
              letterSpacing: 0,
              color: '#322F35',
              ...pillButtonFocusRing,
            }}
          >
            {shareCode || '------'}
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
          }}
        >
          {t('invite.footerHint')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: '16px',
            // Figma `Frame 34` is 48 tall and contains 40-tall button pills
            // centered inside it — the 4 px top/bottom is the M3 state-layer
            // band around each button. Reproduce that band so the visible pill
            // sits at Figma's y=827.1 (= frame top 823.1 + 4 px state-layer top)
            // instead of flush against the row top at y=823.
            alignItems: 'center',
            minHeight: '48px',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              border: '1px solid #CAC4D0',
              color: '#49454F',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
              ...pillButtonFocusRing,
            }}
          >
            {t('invite.back')}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onContinue}
            disabled={!shareCode || bootstrapping}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: !shareCode || bootstrapping ? 'not-allowed' : 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              backgroundColor:
                !shareCode || bootstrapping ? 'rgba(29, 27, 32, 0.12)' : '#4D69B9',
              color: !shareCode || bootstrapping ? '#605D64' : '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor:
                  !shareCode || bootstrapping
                    ? 'rgba(29, 27, 32, 0.12)'
                    : '#3D54A0',
              },
              ...pillButtonFocusRing,
            }}
          >
            {t('invite.continue')}
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message={t('invite.copied')}
      />
    </OnboardingShellV2>
  );
};

export default InviteTeamStep;
